import React from 'react';
import { Card, Stack } from 'react-bootstrap';
import ReactImageFallback from 'react-image-fallback';
import PlaceholderImage from '@/assets/images/placeholders/images/placeholder-image-square.jpg';
import { LomavisShortenedNumber } from '@/lomavisSharedUi/atoms/utils/numbers/LomavisShortenedNumber';
import { LomavisOverlayToolTip1 } from '@/lomavisSharedUi/atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import './LomavisAdCard1.css';

export type LomavisAdCardAnalytics = Record<
  string,
  { value: number | string | null; unit?: string } | number | string | null
>;

export interface LomavisAdCard1Props {
  image?: string;
  title: string;
  subtitle?: string;
  analytics?: LomavisAdCardAnalytics;
}

/**
 * Displays a compact ad/campaign card with a small image, title/subtitle,
 * and a dynamic list of analytics values.
 */
export const LomavisAdCard1: React.FC<LomavisAdCard1Props> = ({
  image,
  title,
  subtitle,
  analytics = {}
}) => {
  const t = translateComponent('LomavisAdCard1');

  const entries = Object.entries(analytics || {});

  return (
    <Card className="h-100 shadow-sm">
      <Card.Body className="d-flex flex-column gap-3">
        <Stack direction="horizontal" gap={2} className="align-items-center">
          <ReactImageFallback
            src={image}
            fallbackImage={PlaceholderImage}
            initialImage={PlaceholderImage}
            alt={title}
            className="rounded-1 w-50xp h-50px"
            style={{ objectFit: 'cover', flexShrink: 0 }}
          />
          <div
            className="d-flex flex-column overflow-hidden"
            style={{ minWidth: 0 }}
          >
            <LomavisOverlayToolTip1 text={title} wrapperClassName="flex-grow-1">
              <div className="fw-bold text-gray-900 text-truncate">{title}</div>
            </LomavisOverlayToolTip1>
            {subtitle && (
              <div className="text-muted small text-truncate">{subtitle}</div>
            )}
          </div>
        </Stack>
        <div className="mt-3 pt-3 border-top">
          <h6 className="text-grey-800 mb-2">{t('adInsights')}</h6>
          <table className="table table-sm table-striped table-borderless border-0">
            <tbody>
              {entries.length > 0 &&
                entries.map(([key, raw]) => {
                  const isObj =
                    raw !== null &&
                    typeof raw === 'object' &&
                    !Array.isArray(raw);
                  const val = isObj ? (raw as { value: number | string | null; unit?: string }).value : raw;
                  const unit = isObj ? (raw as { value: number | string | null; unit?: string }).unit : undefined;

                  return (
                    <tr key={key}>
                      <td className="px-3 fs-6 fw-semibold text-gray-700">
                        {t(key)}
                      </td>
                      <td className="text-end fw-bolder px-3">
                        {typeof val === 'number' ? (
                          (() => {
                            const formattedValue = val.toLocaleString();
                            const tooltipText = unit ? `${formattedValue} ${unit}` : formattedValue;
                            return (
                              <LomavisOverlayToolTip1
                                text={tooltipText}
                                fromCard
                              >
                                <LomavisShortenedNumber value={val} dataUnit={unit} />
                              </LomavisOverlayToolTip1>
                            );
                          })()
                        ) : (
                          (val as string | null | undefined) ?? '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card.Body>
    </Card>
  );
};
